import Layout from "@/components/site/Layout";
import Seo from "@/components/site/Seo";
import ContactSection from "@/components/site/ContactSection";
import { PHONE_DISPLAY, PHONE_TEL, EMAIL } from "@/config/site";
import { localBusinessSchema, breadcrumbSchema } from "@/lib/schema";
import { Phone, Mail, MapPin, Clock } from "lucide-react";

export default function ContactPage() {
  const url = typeof window !== "undefined" ? window.location.href : "/contact";
  const schemas = [
    localBusinessSchema({ url }),
    breadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Contact", url: "/contact" },
    ]),
  ];
  return (
    <Layout>
      <Seo
        title={`Contact BM Towing & Recovery — 24/7 Kirkland, WA`}
        description="Call, text, or message BM Towing & Recovery. 24/7 dispatch across Kirkland, Bellevue, Redmond, Bothell, Kenmore, Woodinville and Issaquah."
        canonical={url}
        jsonLd={schemas}
      />

      {/* Header */}
      <section className="bg-[#0A0A0A] border-b border-yellow-400/30 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="font-display uppercase tracking-[0.3em] text-yellow-400 text-sm">Contact</div>
            <h1 className="mt-2 font-display font-black uppercase text-white text-4xl sm:text-6xl leading-[0.95] tracking-tight">
              Call us, text us, message us.
            </h1>
            <p className="mt-4 text-zinc-300 text-lg max-w-xl">
              We answer the phone 24/7. For anything urgent, please call — it's
              the fastest way to get a truck rolling.
            </p>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid="contact-page-call-btn"
                className="inline-flex items-center gap-3 bg-yellow-400 hover:bg-yellow-500 text-slate-900 font-display font-black uppercase tracking-wider px-5 py-4 border-2 border-black bm-hard-shadow"
              >
                <Phone className="h-5 w-5" strokeWidth={3} /> {PHONE_DISPLAY}
              </a>
              <a
                href={`mailto:${EMAIL}`}
                data-testid="contact-page-email-btn"
                className="inline-flex items-center gap-3 bg-black hover:bg-black text-yellow-400 border-2 border-yellow-400 font-display font-black uppercase tracking-wider px-5 py-4"
              >
                <Mail className="h-5 w-5" strokeWidth={3} /> Email
              </a>
            </div>
          </div>

          <ul className="space-y-4">
            <li className="flex items-start gap-4 bg-black border-2 border-white/10 p-5">
              <div className="h-14 w-14 shrink-0 bg-yellow-400 border-2 border-black flex items-center justify-center">
                <Phone className="h-7 w-7 text-slate-900" strokeWidth={2.5} />
              </div>
              <div>
                <div className="font-display uppercase tracking-widest text-xs text-zinc-500">24/7 Line</div>
                <div className="font-display font-bold text-lg text-white">{PHONE_DISPLAY}</div>
              </div>
            </li>
            <li className="flex items-start gap-4 bg-black border-2 border-white/10 p-5">
              <div className="h-14 w-14 shrink-0 bg-yellow-400 border-2 border-black flex items-center justify-center">
                <Mail className="h-7 w-7 text-slate-900" strokeWidth={2.5} />
              </div>
              <div>
                <div className="font-display uppercase tracking-widest text-xs text-zinc-500">Email</div>
                <div className="font-display font-bold text-lg text-white break-all">{EMAIL}</div>
              </div>
            </li>
            <li className="flex items-start gap-4 bg-black border-2 border-white/10 p-5">
              <div className="h-14 w-14 shrink-0 bg-yellow-400 border-2 border-black flex items-center justify-center">
                <MapPin className="h-7 w-7 text-slate-900" strokeWidth={2.5} />
              </div>
              <div>
                <div className="font-display uppercase tracking-widest text-xs text-zinc-500">Service Base</div>
                <div className="font-display font-bold text-lg text-white">Kirkland, WA — Eastside & Greater Seattle</div>
              </div>
            </li>
            <li className="flex items-start gap-4 bg-black border-2 border-white/10 p-5">
              <div className="h-14 w-14 shrink-0 bg-yellow-400 border-2 border-black flex items-center justify-center">
                <Clock className="h-7 w-7 text-slate-900" strokeWidth={2.5} />
              </div>
              <div>
                <div className="font-display uppercase tracking-widest text-xs text-zinc-500">Hours</div>
                <div className="font-display font-bold text-lg text-white">Open 24 hours · 7 days a week</div>
              </div>
            </li>
          </ul>
        </div>
      </section>

      {/* Map */}
      <section className="py-14 border-b-2 border-slate-800 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="font-display uppercase tracking-[0.3em] text-yellow-400 text-sm">Service Area</div>
          <h2 className="mt-2 font-display font-black uppercase text-white text-3xl sm:text-4xl">
            Based in Kirkland — serving the Eastside
          </h2>
          <div className="mt-6 border-2 border-yellow-400 bm-hard-shadow-yellow overflow-hidden">
            <iframe
              title="BM Towing & Recovery service area — Kirkland, WA"
              data-testid="contact-map-iframe"
              src="https://www.google.com/maps?q=Kirkland%2C%20WA&output=embed"
              width="100%"
              height="450"
              style={{ border: 0, display: "block", background: "#0f172a" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
          <p className="mt-3 text-zinc-500 text-sm">
            We're based in Kirkland, WA and dispatch across Kirkland, Bellevue,
            Redmond, Bothell, Kenmore, Woodinville, and Issaquah.
          </p>
        </div>
      </section>

      <ContactSection />
    </Layout>
  );
}
